import React, { useCallback, useEffect, useState } from 'react';
import { setQuickTourState } from '../services/quickTourState';
import { UNIVERSITY_COUNT } from '../constants/catalog';
import { useNavigate } from 'react-router-dom';
import {
    AcademicCapIcon, ArrowRightIcon, BanknotesIcon, BeakerIcon, BuildingLibraryIcon,
    ChatBubbleLeftRightIcon, LightBulbIcon, MapIcon,
    RocketLaunchIcon, SparklesIcon, SunIcon, UserCircleIcon,
} from '@heroicons/react/24/outline';
import { updateProfileField } from '../services/api';
import { track, EVENTS } from '../services/analytics';
import usePopularQuestions from '../hooks/usePopularQuestions';

// First-login quick tour: a one-time, feature-by-feature walkthrough of the
// whole app, in the order the sidebar (and the journey) flows. It familiarizes
// — the DOING is the Getting started checklist's job afterward. It exists
// because of a real failure mode: a new user added three colleges (burning
// every credit) before uploading a profile — so the profile slide leads and
// the Launchpad slide carries the credit warning.
//
// Shown once per user: AppLayout gates on profile.quick_tour_seen (cross-
// device) with a localStorage belt, same pattern as GettingStartedCard.
// Every exit path (skip, finish, Escape) persists the flag — it never nags.

const seenKey = (email) => `quick_tour_seen_${email}`;

// kicker = the sidebar group the feature lives under, so the tour doubles as
// a map of the navigation.
const SLIDES = [
    {
        key: 'intro',
        kicker: 'Quick tour',
        Icon: SparklesIcon,
        title: 'Welcome to Stratia',
        body: 'Your whole application season in one app, and Astra for questions any time. Here’s a one-minute look at each part. Nothing to fill in yet; the Getting started checklist walks you through the doing afterward.',
    },
    {
        key: 'astra',
        kicker: 'Ask Astra',
        Icon: ChatBubbleLeftRightIcon,
        title: 'Ask Astra, anytime',
        body: 'Astra can see your whole account — profile, college list, fit analyses, roadmap, essays, aid — so you can just ask: “Which of my schools is the biggest stretch?” or “What should I do this week?” She can act, too: add a school to your list, or put a task on your roadmap.',
        note: { tone: 'green', text: 'Before she changes your list or your roadmap, she shows you a card — nothing is added or removed until you say yes.' },
    },
    {
        key: 'profile',
        kicker: 'You',
        Icon: UserCircleIcon,
        title: 'Profile',
        body: 'Type your story in your own words — or upload a transcript or résumé. Every fit, major ranking, essay angle, and suggestion in the app is computed from your profile, so it pays to start here. When you want a second opinion, Sharing lets a parent or counselor see the parts you choose, no account needed.',
        note: { tone: 'green', text: 'The more real your profile, the more real your results.' },
    },
    {
        key: 'major-map',
        kicker: 'Explore',
        stage: 1,
        Icon: AcademicCapIcon,
        title: 'Majors',
        body: 'Ranks the majors your record actually supports — and flags the crowded ones worth thinking twice about — so you apply with a strategy instead of a boilerplate degree.',
    },
    {
        key: 'activities',
        kicker: 'Explore',
        stage: 1,
        Icon: LightBulbIcon,
        title: 'Activities',
        body: 'Extracurriculars matched to your record — each with the why, and a first step you can take this week. They sharpen automatically once your Major Map exists.',
    },
    {
        key: 'universities',
        kicker: 'Explore',
        stage: 1,
        Icon: BuildingLibraryIcon,
        title: 'Universities',
        body: `All ${UNIVERSITY_COUNT} universities, each already scored against your profile — Safety, Target, Reach, or Super Reach — before you add a thing. And the band is judged by the door you’d actually walk through: your in-state rate at a public university, and your major’s own admit rate wherever it’s admitted separately. For most schools you also get real reported decisions — GPA, SAT, round, verdict — from students who applied.`,
        note: { tone: 'green', text: 'Georgia Tech admits Georgia residents at about 29%, not the 13% headline. If that’s you, your odds say so.' },
    },
    {
        key: 'programs',
        kicker: 'Explore',
        stage: 1,
        Icon: SunIcon,
        title: 'Programs',
        body: 'A hand-verified catalog of summer research, academic, and leadership programs — picked against your record, with deadlines, real costs, and how hard each is to get into. One click puts a deadline on your roadmap before it slips.',
    },
    {
        key: 'launchpad',
        kicker: 'Plan',
        stage: 2,
        Icon: RocketLaunchIcon,
        title: 'Launchpad',
        body: 'Home for your college list. Every school you add gets the deep read: a full fit analysis, your chances by major, essay angles, and where you stand on cost. “Find similar” grows the list from any school you love. Strategy pulls it all into one written plan across your whole list.',
        note: { tone: 'amber', text: 'Build your profile first — an analysis of an empty profile can only give you generic answers.' },
    },
    {
        key: 'roadmap',
        kicker: 'Plan',
        stage: 2,
        Icon: MapIcon,
        title: 'Roadmap',
        // No "weekly email" here: the digest reaches paying accounts only,
        // and the tour speaks to every new account (#4871; interface-copy
        // rule 4 keeps plan names off this surface, so the clause is gone,
        // not qualified).
        body: 'One timeline anchored to your graduation year — deadlines, essays, scholarships, and tasks, paced to where you are right now.',
    },
    {
        key: 'decide',
        kicker: 'Decide',
        stage: 3,
        Icon: BanknotesIcon,
        title: 'Decisions & Aid',
        body: 'When results arrive, record them — one line per school. Aid puts your offers side by side, so “which one can we actually afford” has an answer instead of an argument. Decisions you share, anonymized, are what power the real numbers every student here sees.',
    },
    {
        key: 'wrap',
        // Learn left the signed-in sidebar (#5309), and the kicker names a
        // section the student can find there.
        kicker: 'Connect',
        Icon: BeakerIcon,
        title: 'Bring your own AI — and you’re set',
        body: 'Already use Claude, ChatGPT, Glean, or Copilot? Connect it to your Stratia data and work from there, run bigger questions from the Playbook, and keep anything worth saving in your Notebook. Resources and Video Tours go deeper when you want them.',
        // the Playbook retired (#5258, astra_popular_questions on): its prompts live on Connect
        bodyNoPlaybook: 'Already use Claude, ChatGPT, Glean, or Copilot? Connect it to your Stratia data and work from there, pick a ready-made prompt on Connect, and keep anything worth saving in your Notebook. Resources and Video Tours go deeper when you want them.',
        note: { tone: 'green', text: 'The Getting started checklist at the top of every page tracks your next step. It starts with your profile — and Astra is one click away on every screen.' },
    },
];

const NOTE_TONES = {
    green: 'bg-[#F0F7F1] border-[#CFE3D4] text-[#1A4D2E]',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
};

export default function QuickTour({ email, onClose }) {
    const [idx, setIdx] = useState(0);
    // Every copy of the tour (AppLayout's first-run one, Settings' replay)
    // holds Astra Today's notification ask off while it is up (#4234).
    useEffect(() => {
        setQuickTourState('open');
        return () => setQuickTourState('none');
    }, []);
    const navigate = useNavigate();
    const { on: playbookRetired } = usePopularQuestions();
    const raw = SLIDES[idx];
    const slide = playbookRetired && raw.bodyNoPlaybook ? { ...raw, body: raw.bodyNoPlaybook } : raw;
    const last = idx === SLIDES.length - 1;

    const persistSeen = useCallback(() => {
        try { localStorage.setItem(seenKey(email), '1'); } catch { /* private mode */ }
        updateProfileField(email, 'quick_tour_seen', true).catch(() => {});
    }, [email]);

    const skip = useCallback(() => {
        persistSeen();
        track(EVENTS.QUICK_TOUR_SKIPPED, { slide: SLIDES[idx].key });
        onClose();
    }, [persistSeen, idx, onClose]);

    const finish = useCallback((cta) => {
        persistSeen();
        track(EVENTS.QUICK_TOUR_COMPLETED, { cta });
        onClose();
        if (cta === 'profile') navigate('/profile');
    }, [persistSeen, onClose, navigate]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') skip(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [skip]);

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="quick-tour">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Quick tour"
                className="relative w-full max-w-xl bg-[#FDFCF7] rounded-2xl border border-[#E0DED8] shadow-2xl p-6 sm:p-8"
            >
                <div className="flex items-center justify-between mb-5">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[#9A968C]">
                        {idx + 1} of {SLIDES.length}
                    </span>
                    <button
                        type="button"
                        onClick={skip}
                        data-testid="quick-tour-skip"
                        className="text-xs font-medium text-[#9A968C] hover:text-[#4A4A4A] transition-colors"
                    >
                        Skip tour
                    </button>
                </div>

                <div className="flex items-start gap-4 mb-4">
                    <div className="h-12 w-12 rounded-xl bg-[#F0F7F1] border border-[#CFE3D4] flex items-center justify-center shrink-0">
                        <slide.Icon className="h-6 w-6 text-[#1A4D2E]" />
                    </div>
                    <div>
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[#1A4D2E] mb-0.5 flex items-center gap-1.5">
                            {/* The sidebar numbers its three journey stages;
                                the tour repeats the number so a student can
                                match slide to nav section at a glance. */}
                            {slide.stage && (
                                <span className="inline-grid place-items-center h-[15px] w-[15px] rounded bg-[#1A4D2E] text-white text-[9px] font-bold"
                                      aria-hidden="true"
                                      data-testid="tour-stage-number">{slide.stage}</span>
                            )}
                            {slide.kicker}
                        </p>
                        <h2 className="font-serif text-xl font-bold text-[#2C2C2C]">{slide.title}</h2>
                    </div>
                </div>

                <p className="text-sm text-[#4A4A4A] leading-relaxed mb-3">{slide.body}</p>

                {slide.note && (
                    <div className={`text-xs font-medium border rounded-lg px-3 py-2 mb-3 ${NOTE_TONES[slide.note.tone]}`}>
                        {slide.note.text}
                    </div>
                )}

                <div className="flex items-center justify-between mt-6">
                    <div className="flex items-center gap-1.5" aria-hidden="true">
                        {SLIDES.map((s, i) => (
                            <span key={s.key} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-[#1A4D2E]' : 'w-1.5 bg-[#E0DED8]'}`} />
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        {idx > 0 && (
                            <button
                                type="button"
                                onClick={() => setIdx(idx - 1)}
                                className="px-4 py-2 text-sm font-medium text-[#4A4A4A] hover:text-[#1A4D2E] transition-colors"
                            >
                                Back
                            </button>
                        )}
                        {last ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => finish('explore')}
                                    data-testid="quick-tour-explore"
                                    className="px-4 py-2 text-sm font-medium text-[#4A4A4A] hover:text-[#1A4D2E] transition-colors"
                                >
                                    I’ll explore on my own
                                </button>
                                <button
                                    type="button"
                                    onClick={() => finish('profile')}
                                    data-testid="quick-tour-finish"
                                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A4D2E] text-white text-sm font-semibold rounded-lg hover:bg-[#2D6B45] transition-colors"
                                >
                                    Start with my profile
                                    <ArrowRightIcon className="h-4 w-4" />
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIdx(idx + 1)}
                                data-testid="quick-tour-next"
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1A4D2E] text-white text-sm font-semibold rounded-lg hover:bg-[#2D6B45] transition-colors"
                            >
                                Next
                                <ArrowRightIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
